// Admin Genel Bakış — sistem geneli (tüm klinikler). Analiz dili
// (design-principles §4): başlık şeridi → slate50 zemin → KPI satırı → sabit
// gövdeli kartlar. Yumuşak tipografi: 800/900 kalınlık yok. Token renkleri
// yalnız yönü anlatır (mavi giriş / kırmızı çıkış — tokenChartTheme).
import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Avatar, Skeleton, alpha } from "@mui/material";
import {
  Dashboard as DashboardIcon,
  LocalHospital as ClinicIcon,
  DevicesOther as DeviceIcon,
  Token as TokenIcon,
  SwapVert as FlowIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from "@mui/icons-material";
import { useClinicTheme } from "../theme";
import { COLORS, TYPO, CARD_PAD, CARD_GAP, CARD_PAGE_PAD, dashboardSectionSx } from "../styles/uiConstants";
import useAuth from "../hooks/useAuth";
import useDashboardSocket from "../hooks/useDashboardSocket";
import EmptyState from "../components/atoms/EmptyState/EmptyState";
import SectionCard from "../components/molecules/SectionCard";
import PageHeaderBar from "../components/molecules/PageHeaderBar";
import KpiTile from "../components/molecules/KpiTile";
import PeriodSelector, { DASHBOARD_PERIODS } from "../components/common/PeriodSelector";
import MonthlyFlowChart from "../components/organisms/charts/MonthlyFlowChart";
import { FLOW_COLORS } from "../components/organisms/charts/tokenChartTheme";
import {
  useLazyGetAdminStatsQuery,
  useLazyGetAdminTokenTrendQuery,
  useLazyGetAdminTokenBreakdownQuery,
  useLazyGetAdminClinicRankingQuery,
  useLazyGetAdminPatientEcosystemQuery,
  useLazyGetAdminSystemHealthQuery,
  useLazyGetAdminChurnRiskQuery,
} from "../store/dashboardApi";

const MONTH_NAMES_TR = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık",
];

const TOP_BODY_HEIGHT = 340;
const BOTTOM_BODY_HEIGHT = 300;

const nf = (n) => Number(n || 0).toLocaleString("tr-TR");
const pct = (n, total) => (total ? Math.round((n / total) * 100) : 0);

/** Kart: başlık + gövde (dar ekranda sabit yükseklik, genişte satırı doldurur; design-principles §4). */
function DashCard({ icon, title, subtitle, action, bodyHeight, flush = false, children }) {
  return (
    <SectionCard
      icon={icon}
      title={title}
      subtitle={subtitle}
      action={action}
      sx={{ ...dashboardSectionSx, display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0 }}
    >
      {/* Geniş ekranda gövde satırın kalan yüksekliğini doldurur (pano ekrana sığar);
          dar ekranda sabit yükseklik + sayfa kaydırma. */}
      <Box
        sx={{
          p: flush ? 0 : CARD_PAD,
          height: { xs: bodyHeight, lg: "auto" },
          flex: { lg: 1 },
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        {children}
      </Box>
    </SectionCard>
  );
}

function CardEmpty({ icon, title, description }) {
  return (
    <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <EmptyState variant="illustrated-sm" icon={icon} title={title} description={description} sx={{ py: 0 }} />
    </Box>
  );
}

/** Önceki döneme göre yüzde değişim (sunucu calcTrend: { value: %, direction }). */
function TrendText({ trend, goodWhenUp = true }) {
  if (!trend || trend.direction === "neutral") {
    return <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate400 }}>Değişim yok</Typography>;
  }
  const good = goodWhenUp ? trend.direction === "up" : trend.direction === "down";
  const Icon = trend.direction === "up" ? TrendingUpIcon : TrendingDownIcon;
  return (
    <Typography
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.25,
        fontSize: TYPO.caption,
        fontWeight: 600,
        color: good ? COLORS.successDark : COLORS.errorDark,
      }}
    >
      <Icon sx={{ fontSize: 14 }} />%{nf(trend.value)}
    </Typography>
  );
}

/** Yatay çubuk satırı: etiket solda, değer + yüzde sağda, altında 8px çubuk. */
function BarRow({ label, value, total, color, meta }) {
  return (
    <Box sx={{ py: 1.1 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1, mb: 0.6 }}>
        <Typography noWrap sx={{ fontSize: TYPO.body, color: COLORS.slate700, minWidth: 0 }}>
          {label}
          {meta ? (
            <Box component="span" sx={{ color: COLORS.slate400, ml: 0.75, fontSize: TYPO.caption }}>
              {meta}
            </Box>
          ) : null}
        </Typography>
        <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
          {nf(value)}
          <Box component="span" sx={{ fontWeight: 400, color: COLORS.slate400, ml: 0.75, fontSize: TYPO.caption }}>
            %{pct(value, total)}
          </Box>
        </Typography>
      </Box>
      <Box sx={{ height: 8, borderRadius: 4, bgcolor: COLORS.slate100, overflow: "hidden" }}>
        <Box sx={{ width: `${Math.max(value ? 2 : 0, pct(value, total))}%`, height: "100%", borderRadius: 4, bgcolor: color }} />
      </Box>
    </Box>
  );
}

function RowsSkeleton({ rows = 4, bars = false }) {
  return (
    <Box>
      {[...Array(rows)].map((_, i) => (
        <Box key={i} sx={{ py: 1.1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: bars ? 0.6 : 0 }}>
            <Skeleton width={`${35 + ((i * 17) % 30)}%`} />
            <Skeleton width={56} />
          </Box>
          {bars ? <Skeleton variant="rounded" height={8} width={`${90 - i * 18}%`} sx={{ borderRadius: 4 }} /> : null}
        </Box>
      ))}
    </Box>
  );
}

const SK_FLOW = [60, 45, 75, 50, 85, 65];
function FlowSkeleton() {
  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0, px: 2 }}>
      <Box sx={{ flex: 1, display: "flex", alignItems: "flex-end", gap: 4 }}>
        {SK_FLOW.map((h, i) => (
          <Skeleton key={i} variant="rounded" sx={{ flex: 1, height: `${h}%`, borderRadius: "4px 4px 0 0" }} />
        ))}
      </Box>
      <Box sx={{ borderTop: `1px solid ${COLORS.slate200}` }} />
      <Box sx={{ flex: 0.5, display: "flex", alignItems: "flex-start", gap: 4 }}>
        {SK_FLOW.map((h, i) => (
          <Skeleton key={i} variant="rounded" sx={{ flex: 1, height: `${h * 0.6}%`, borderRadius: "0 0 4px 4px" }} />
        ))}
      </Box>
    </Box>
  );
}

export default function Dashboard() {
  const { palette } = useClinicTheme();
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const primary = palette.primary;

  const { stats: liveStats } = useDashboardSocket("admin");

  const [fetchStats, { data: statsData }] = useLazyGetAdminStatsQuery();
  const [fetchTokenTrend, { data: tokenTrendData }] = useLazyGetAdminTokenTrendQuery();
  const [fetchClinicRanking, { data: clinicRankingData }] = useLazyGetAdminClinicRankingQuery();
  const [fetchPatientEcosystem, { data: ecosystemData }] = useLazyGetAdminPatientEcosystemQuery();
  const [fetchSystemHealth, { data: healthData }] = useLazyGetAdminSystemHealthQuery();
  const [fetchChurnRisk, { data: churnData }] = useLazyGetAdminChurnRiskQuery();
  const [fetchTokenBreakdown, { data: breakdownData, isFetching: breakdownFetching }] =
    useLazyGetAdminTokenBreakdownQuery();

  // Token hareketleri kartının dönem filtresi — diğer kartlardan bağımsız.
  const [breakdownPeriod, setBreakdownPeriod] = useState("today");

  useEffect(() => {
    if (!token) return;
    fetchStats({ token });
    fetchTokenTrend({ token });
    fetchClinicRanking({ token });
    fetchPatientEcosystem({ token });
    fetchSystemHealth({ token });
    fetchChurnRisk({ token });
  }, [token, fetchStats, fetchTokenTrend, fetchClinicRanking, fetchPatientEcosystem, fetchSystemHealth, fetchChurnRisk]);

  useEffect(() => {
    if (!token) return;
    fetchTokenBreakdown({ token, period: breakdownPeriod });
  }, [token, breakdownPeriod, fetchTokenBreakdown]);

  const stats = liveStats || statsData?.data;
  const tokenTrend = tokenTrendData?.data;
  const clinicRanking = clinicRankingData?.data;
  const ecosystem = ecosystemData?.data;
  const health = healthData?.data;
  const churn = churnData?.data;
  const breakdown = breakdownData?.data;

  // Cihazlar BLE ile telefona bağlanır — online/offline bilgisi YOK; durum
  // dağılımı sunucudaki `getSystemHealth().devices` alanından gelir.
  const deviceCounts = health?.devices;
  const deviceTotal = deviceCounts?.total ?? 0;

  // Aylık akış: sunucu son 6 ayı sırayla döner; ay anahtarını burada üretiriz.
  const flowData = useMemo(() => {
    const rows = tokenTrend?.data || [];
    const now = new Date();
    return rows.map((r, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (rows.length - 1 - i), 1);
      const inflow = (r.paid || 0) + (r.free || 0);
      const outflow = r.consumed || 0;
      return { month: `${d.getFullYear()}-${d.getMonth() + 1}`, inflow, outflow, net: inflow - outflow };
    });
  }, [tokenTrend]);

  const items = breakdown?.items || [];
  const itemOf = (key) => items.find((it) => it.key === key);
  const net = itemOf("net");
  const breakdownPending = !breakdown || breakdownFetching;

  const ranking = (clinicRanking || []).slice(0, 5);
  const rankingMax = Math.max(1, ...ranking.map((c) => c.totalConsumed ?? c.tokens ?? 0));
  const currentMonthName = MONTH_NAMES_TR[new Date().getMonth()];
  const periodLabel = DASHBOARD_PERIODS.find((p) => p.value === breakdownPeriod)?.label || "Bugün";

  return (
    <Box sx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column", bgcolor: COLORS.slate50 }}>
      <PageHeaderBar
        icon={<DashboardIcon sx={{ fontSize: 22 }} />}
        title="Genel Bakış"
        subtitle="Tüm klinikler · sistem geneli"
        action={
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.25,
              py: 0.4,
              borderRadius: 999,
              fontSize: TYPO.label,
              fontWeight: 500,
              color: COLORS.successDark,
              bgcolor: alpha(COLORS.success, 0.1),
            }}
          >
            <Box
              component="span"
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                bgcolor: COLORS.success,
                "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.35 } },
                animation: "pulse 2s ease-in-out infinite",
                "@media (prefers-reduced-motion: reduce)": { animation: "none" },
              }}
            />
            Canlı
          </Box>
        }
      />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", ...CARD_PAGE_PAD }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: CARD_GAP, height: { lg: "100%" } }}>
          {/* ── Özet ── */}
          <Box sx={{ display: "grid", gap: CARD_GAP, gridTemplateColumns: { xs: "1fr 1fr", lg: "repeat(4, 1fr)" } }}>
            <KpiTile
              loading={!ecosystem}
              label="Toplam hasta"
              value={nf(ecosystem?.total)}
              hint={`Kliniğe bağlı ${nf(ecosystem?.clinicBound)} · Bağımsız ${nf(ecosystem?.independent)}`}
            />
            {/* Aktif klinikte trend yok: sunucu bu alanı sabit 0/neutral döndürüyor */}
            <KpiTile loading={!stats} label="Aktif klinik" value={nf(stats?.activeClinics?.value)} hint="Sistemde aktif" />
            <KpiTile
              loading={!health}
              label="Kayıtlı cihaz"
              value={nf(deviceTotal)}
              hint={`${nf(deviceCounts?.Hastada)} hastada · ${nf(deviceCounts?.Depo)} depoda`}
            />
            <KpiTile
              loading={!churn}
              label="Risk altındaki klinik"
              value={nf(churn?.total)}
              valueColor={churn?.total ? COLORS.errorDark : undefined}
              hint="7+ gündür giriş yapılmadı"
            />
          </Box>

          {/* ── Token akışı | Token hareketleri ── */}
          <Box sx={{ display: "grid", gap: CARD_GAP, flex: { lg: 1 }, minHeight: { lg: 240 }, gridTemplateColumns: { xs: "1fr", lg: "1.4fr 1fr" } }}>
            <DashCard
              icon={<TokenIcon sx={{ fontSize: 18 }} />}
              title="Token akışı"
              subtitle="Son 6 ayın yükleme ve tüketimi · ay altında net değişim"
              bodyHeight={TOP_BODY_HEIGHT}
            >
              {!tokenTrend ? (
                <FlowSkeleton />
              ) : flowData.some((r) => r.inflow || r.outflow) ? (
                <MonthlyFlowChart data={flowData} height="fill" />
              ) : (
                <CardEmpty
                  icon={<TokenIcon />}
                  title="Henüz token hareketi yok"
                  description="Klinikler token yükledikçe ve harcadıkça aylık akış burada görünür."
                />
              )}
            </DashCard>

            <DashCard
              icon={<FlowIcon sx={{ fontSize: 18 }} />}
              title="Token hareketleri"
              subtitle={`${periodLabel} · önceki döneme göre değişimle`}
              action={
                <PeriodSelector
                  variant="segmented"
                  options={DASHBOARD_PERIODS}
                  value={breakdownPeriod}
                  onChange={setBreakdownPeriod}
                  palette={palette}
                  isLoading={breakdownPending}
                />
              }
              bodyHeight={TOP_BODY_HEIGHT}
            >
              {breakdownPending ? (
                <RowsSkeleton rows={4} />
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                  {[
                    { key: "paid", label: "Fatura", hint: "Ücretli yükleme", color: FLOW_COLORS.in },
                    { key: "free", label: "Bedelsiz yükleme", hint: "Ücretsiz tanımlanan", color: FLOW_COLORS.in },
                    { key: "consumed", label: "Tüketim", hint: "Kliniklerin harcadığı", color: FLOW_COLORS.out, upIsBad: true },
                  ].map((row) => {
                    const it = itemOf(row.key);
                    return (
                      <Box
                        key={row.key}
                        sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.4, borderBottom: `1px solid ${COLORS.slate100}` }}
                      >
                        <Box sx={{ width: 10, height: 10, borderRadius: "3px", bgcolor: row.color, flexShrink: 0 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate800 }}>{row.label}</Typography>
                          <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate500 }}>{row.hint}</Typography>
                        </Box>
                        <Box sx={{ textAlign: "right" }}>
                          <Typography sx={{ fontSize: "1.0625rem", fontWeight: 600, color: COLORS.slate800, fontVariantNumeric: "tabular-nums" }}>
                            {nf(it?.value)}
                          </Typography>
                          <TrendText trend={it?.trend} goodWhenUp={!row.upIsBad} />
                        </Box>
                      </Box>
                    );
                  })}
                  {/* Net — dolaşımdaki değişim; işarete göre yön rengi */}
                  <Box sx={{ mt: "auto", pt: 2, display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                    <Box>
                      <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500 }}>Net değişim</Typography>
                      <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate400 }}>Yükleme − tüketim</Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "1.75rem",
                        fontWeight: 700,
                        fontVariantNumeric: "tabular-nums",
                        color: !net?.value ? COLORS.slate700 : net.value > 0 ? FLOW_COLORS.in : FLOW_COLORS.out,
                      }}
                    >
                      {net?.value > 0 ? "+" : ""}
                      {nf(net?.value)}
                    </Typography>
                  </Box>
                </Box>
              )}
            </DashCard>
          </Box>

          {/* ── Cihaz durumu | Klinik performansı ── */}
          <Box sx={{ display: "grid", gap: CARD_GAP, flex: { lg: 1 }, minHeight: { lg: 240 }, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
            <DashCard
              icon={<DeviceIcon sx={{ fontSize: 18 }} />}
              title="Cihaz durumu"
              subtitle="Sisteme kayıtlı ölçüm cihazlarının dağılımı"
              bodyHeight={BOTTOM_BODY_HEIGHT}
            >
              {!health ? (
                <RowsSkeleton rows={3} bars />
              ) : deviceTotal === 0 ? (
                <CardEmpty
                  icon={<DeviceIcon />}
                  title="Kayıtlı cihaz yok"
                  description="Cihazlar mobil uygulamadan admin girişiyle sisteme tanıtılır."
                />
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1 }}>
                  <BarRow label="Hastada" meta="kullanımda" value={deviceCounts?.Hastada ?? 0} total={deviceTotal} color={primary} />
                  <BarRow label="Depoda" meta="atanmamış" value={deviceCounts?.Depo ?? 0} total={deviceTotal} color={primary} />
                  <BarRow label="Serviste" meta="bakımda" value={deviceCounts?.Servis ?? 0} total={deviceTotal} color={primary} />
                </Box>
              )}
            </DashCard>

            <DashCard
              icon={<ClinicIcon sx={{ fontSize: 18 }} />}
              title={`${currentMonthName} klinik performansı`}
              subtitle="Bu ay en çok token tüketen 5 klinik"
              bodyHeight={BOTTOM_BODY_HEIGHT}
            >
              {!clinicRanking ? (
                <RowsSkeleton rows={5} bars />
              ) : ranking.length === 0 ? (
                <CardEmpty
                  icon={<ClinicIcon />}
                  title={`${currentMonthName} ayı için veri yok`}
                  description="Bu ay token tüketen klinik oldukça sıralama burada görünür."
                />
              ) : (
                <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overscrollBehavior: "contain" }}>
                  {ranking.map((c, i) => {
                    const used = c.totalConsumed ?? c.tokens ?? 0;
                    return (
                      <Box key={c._id || i} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
                        <Avatar sx={{ width: 30, height: 30, fontSize: 12, fontWeight: 600, bgcolor: alpha(primary, 0.1), color: primary }}>
                          {(c.name || "?").charAt(0).toLocaleUpperCase("tr")}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1, mb: 0.5 }}>
                            <Typography noWrap sx={{ fontSize: TYPO.body, color: COLORS.slate800, minWidth: 0 }}>
                              {c.name}
                              <Box component="span" sx={{ color: COLORS.slate400, ml: 0.75, fontSize: TYPO.caption }}>
                                {nf(c.patientCount ?? c.patients)} hasta
                              </Box>
                            </Typography>
                            <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                              {nf(used)}
                              <Box component="span" sx={{ fontWeight: 400, color: COLORS.slate400, ml: 0.5, fontSize: TYPO.caption }}>
                                token
                              </Box>
                            </Typography>
                          </Box>
                          {/* Sıralama uzunlukla okunur; renk tek (design-principles §6) */}
                          <Box sx={{ height: 6, borderRadius: 3, bgcolor: COLORS.slate100, overflow: "hidden" }}>
                            <Box sx={{ width: `${(used / rankingMax) * 100}%`, height: "100%", borderRadius: 3, bgcolor: FLOW_COLORS.out }} />
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </DashCard>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
